// MARK://dhk8/admin-shell/pages/modules/CategoryDetailPage
// Ficha "ver-primero" de una categoría del catálogo (barrido a ficha de detalle,
// Gustavo 2026-06-23). Header con nombre + orden/estación + pill de estado + KPI
// (productos en la categoría) + Tabs (General, Productos, Historial). El tab General
// se ve primero; "Editar" abre el SectionedSheet con el MISMO form que el modal de
// la lista (Identidad + Operación/estación). No hay getCategory singular en el
// client → se carga listCategories() y se hace find(id), igual que ProductDetailPage
// resuelve la receta por la lista.
import { useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ChevronLeft, Pencil } from 'lucide-react'

import * as cat from '../../lib/clients/catalog.js'
import { listStations, type PrintStation } from '../../lib/clients/print-stations.js'
import type { Category } from '../../lib/mock-data.js'
import { useAsync, asyncState } from '../../lib/use-async.js'
import { mxn } from '../../lib/money.js'
import EntityDetailPage from '../../components/EntityDetailPage.js'
import StatusPill, { lifecycleTone } from '../../components/StatusPill.js'
import { SummaryStrip } from '../../components/StatCard.js'
import Tabs from '../../components/Tabs.js'
import SectionedSheet, { FormSection } from '../../components/SectionedSheet.js'
import AuditTrail from '../../components/AuditTrail.js'
import { useToast } from '../../components/Toast.js'

type Tab = 'general' | 'productos' | 'historial'

export default function CategoryDetailPage() {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const { toast } = useToast()

  // No hay getCategory(id): se carga la lista y se hace find (como ProductDetailPage
  // con recetas). listProducts da los productos de la categoría; listStations el
  // nombre de la estación de impresión.
  const cats = useAsync((s) => cat.listCategories(s))
  const prods = useAsync((s) => cat.listProducts(s))
  const stations = useAsync((s) => listStations(s))

  const [tab, setTab] = useState<Tab>('general')
  const [editing, setEditing] = useState(false)
  const [form, setForm] = useState<Category | null>(null)
  const [busy, setBusy] = useState(false)

  const c = useMemo(() => (cats.data ?? []).find((x) => x.id === id) ?? null, [cats.data, id])
  const stationName = (sid: string | null | undefined): string | null => {
    if (!sid) return null
    return stations.data?.find((s: PrintStation) => s.id === sid)?.name ?? '(estación no encontrada)'
  }
  const sName = c ? stationName(c.stationId) : null

  // Productos de esta categoría.
  const categoryProducts = useMemo(() => (prods.data ?? []).filter((p) => p.categoryId === id), [prods.data, id])

  function openEdit() { if (c) { setForm({ ...c, stationId: c.stationId ?? null }); setEditing(true) } }
  async function save() {
    if (!form) return
    setBusy(true)
    try { await cat.updateCategory(form); toast('Categoría actualizada', 'success'); setEditing(false); cats.reload() }
    catch (e) { toast(e instanceof Error ? e.message : 'No se pudo guardar', 'danger') } finally { setBusy(false) }
  }

  const kpis = useMemo(() => {
    if (!c) return []
    return [
      { label: 'Productos', value: categoryProducts.length },
      { label: 'Orden', value: c.sortOrder },
    ]
  }, [c, categoryProducts.length])

  return (
    <div className="adm-page">
      <div className="adm-page__head">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button className="adm-btn adm-btn--ghost adm-btn--sm" onClick={() => navigate('/catalog')} aria-label="Volver al catálogo"><ChevronLeft size={18} /></button>
          <div><h1 className="adm-page__title">Categoría</h1><p className="adm-page__sub">Catálogo · ficha de la categoría</p></div>
        </div>
      </div>

      {asyncState(cats.loading, cats.error, cats.reload)}
      {!cats.loading && !c && <div className="adm-empty">Categoría no encontrada.</div>}

      {c && (
        <EntityDetailPage
          title={c.name}
          subtitle={`Orden ${c.sortOrder}${sName ? ` · estación: ${sName}` : ' · sin estación'}`}
          pills={<span style={{ display: 'inline-flex', gap: 6 }}>
            <StatusPill tone={lifecycleTone(c.active)} label={c.active ? 'Activa' : 'Inactiva'} />
            {!c.publicVisible && <StatusPill tone="neutral" label="Interna" />}
          </span>}
          kpis={<SummaryStrip items={kpis} />}
          actions={<button className="adm-btn adm-btn--primary adm-btn--sm" onClick={openEdit}><Pencil size={15} /> Editar</button>}
          tabs={<Tabs
            tabs={[
              { id: 'general', label: 'General' },
              { id: 'productos', label: 'Productos', count: categoryProducts.length },
              { id: 'historial', label: 'Historial' },
            ]}
            active={tab}
            onChange={(t) => setTab(t as Tab)}
            ariaLabel="Secciones de la categoría"
          />}
        >
          {tab === 'general' && (
            <div className="adm-card adm-card--pad" style={{ display: 'grid', gap: 14, maxWidth: 560 }}>
              <Field label="Nombre">{c.name}</Field>
              <Field label="Orden">{c.sortOrder}</Field>
              <Field label="Estado">{c.active ? 'Activa' : 'Inactiva'}</Field>
              <Field label="Visibilidad">{c.publicVisible ? 'Pública · aparece en el menú del comensal' : 'Interna · solo POS/recetas/inventario (oculta al comensal)'}</Field>
              <Field label="Estación de impresión">{sName ?? <span className="adm-muted">Sin estación</span>}</Field>
            </div>
          )}

          {tab === 'productos' && (
            <div className="adm-card adm-card--pad0">
              {asyncState(prods.loading, prods.error, prods.reload)}
              <div className="adm-list">
                {!prods.loading && categoryProducts.length === 0 && <div className="adm-empty">Sin productos en esta categoría.</div>}
                {categoryProducts.map((p) => (
                  <div key={p.id} className="adm-row" role="button" tabIndex={0} style={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/catalog/products/${p.id}`)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(`/catalog/products/${p.id}`) } }}>
                    <div className="adm-row__main">
                      <div className="adm-row__title">{p.name}</div>
                      <div className="adm-row__meta">SKU {p.sku || '—'}</div>
                    </div>
                    <div className="adm-row__tail">
                      <StatusPill tone={lifecycleTone(p.available)} label={p.available ? 'Disponible' : 'Agotado'} />
                      <span className="adm-mono">{mxn(p.priceCents)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {tab === 'historial' && <AuditTrail entityId={c.id} />}
        </EntityDetailPage>
      )}

      {form && (
        <SectionedSheet
          open={editing}
          title="Editar categoría"
          onClose={() => setEditing(false)}
          footer={<>
            <button className="adm-btn" onClick={() => setEditing(false)} disabled={busy}>Cancelar</button>
            <button className="adm-btn adm-btn--primary" onClick={save} disabled={busy || !form.name.trim()}>{busy ? 'Guardando…' : 'Guardar'}</button>
          </>}
        >
          <FormSection title="Identidad">
            <label className="adm-field"><span className="adm-field__label">Nombre</span><input className="adm-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></label>
            <label className="adm-field"><span className="adm-field__label">Orden</span><input className="adm-input" type="number" value={form.sortOrder} onChange={(e) => setForm({ ...form, sortOrder: Number(e.target.value) })} /></label>
            <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Activa</label>
            <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
              <input type="checkbox" checked={form.publicVisible} onChange={(e) => setForm({ ...form, publicVisible: e.target.checked })} style={{ marginTop: 3 }} />
              <span>Visible en menú público
                <span className="adm-row__meta" style={{ display: 'block' }}>Apágalo para categorías internas (p.ej. insumos): siguen en POS/recetas/inventario pero no las ve el comensal.</span>
              </span>
            </label>
          </FormSection>
          <FormSection title="Operación">
            <label className="adm-field">
              <span className="adm-field__label">Estación de impresión</span>
              <select className="adm-select" value={form.stationId ?? ''} onChange={(e) => setForm({ ...form, stationId: e.target.value || null })} disabled={stations.loading}>
                <option value="">Sin estación</option>
                {(stations.data ?? []).map((s: PrintStation) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
              <span className="adm-row__meta" style={{ marginTop: 4 }}>Productos de esta categoría se enrutan a esta estación (cocina, barra…).</span>
            </label>
          </FormSection>
        </SectionedSheet>
      )}
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div style={{ fontSize: 12, color: 'var(--pos-fg-tertiary)', marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 15, fontWeight: 500, color: 'var(--pos-fg)' }}>{children}</div>
    </div>
  )
}
